import React, {useEffect, useRef, useState} from 'react';
import BaseSimpleItemEditorForm from "../BaseSimplePage/BaseSimpleItemEditorForm";
import SuggestionBlock from "./SuggestionBlock";
import InfoBlock from "./InfoBlock";
import FormBlock from "../../formComponents/FormBlock";
import {Alert, InputAdornment, LinearProgress, TextField, useTheme} from "@mui/material";
import ItemBrowserHeaderSimpleSelect from "../../components/ItemBrowserHeaderSimpleSelect";
import {allArtistRoles, allSuggestArtistRoles} from "../../data/Enums";
import ServerManager from "../../API/ServerManager";
import blockCl from "../../formComponents/FormBlock.module.css";
import cl from "./ArtistSuggestionsItemForm.module.css";
import ArtistItem from "./ArtistItem";
import {getFullName} from "../../utils/PersonNameUtil";

const ITEMS_PER_RQ_PAGE = 30;
const TYPING_TIMEOUT = 500; // [ms]

const ArtistSuggestionsItemFormStep2 = ({initData, controlRef}) => {

    const theme = useTheme();

    const [searchText, setSearchText] = useState(initData.fio ?? '');
    const [mainRoleId, setMainRoleId] = useState(allSuggestArtistRoles.find(x => x.id === initData?.role) ? initData?.role : 0);
    const rqIdCounter = useRef(0);
    const [status, setStatus] = useState(""); // "", idle, mainLoad, addLoad, mainError, typing
    const [mainErrorMessage, setMainErrorMessage] = useState("");
    const [lastTypingTime, setLastTypingTime] = useState(0);
    const [totalItemCount, setTotalItemCount] = useState(-1);

    const [filteredItems, setFilteredItems] = useState([]); // текущие загруженные с сервера (с учетом фильтра) итемы (некоторая их часть - страница)
    const [filterExtraParams, setFilterExtraParams] = useState(mainRoleId === 0 ? { main_role: "writer,composer,painter" } : { main_role: mainRoleId });
    const [refsData, setRefsData] = useState({});

    const [selectedItemId, setSelectedItemId] = useState(0);

    useEffect(() => {
        if (controlRef) {
            controlRef.current = {
                getLinkedArtistId: getLinkedArtistId,
            };
        }
    });

    function getLinkedArtistId() {
        return selectedItemId;
    }

    function onFilterEdit(forceUpdate = false) {
        setStatus("typing");
        setLastTypingTime(forceUpdate ? 0 : Date.now());
        setFilteredItems([]);
        setSelectedItemId(0);
        setTotalItemCount(-1);
        rqIdCounter.current++;
    }

    // после первого рендера сразу запрашиваем первую порцию
    useEffect(() => {
        requestMainItems();

        // при анмоунте отменяем текущий запрос
        return () => {
            rqIdCounter.current++;
        }
    }, []);

    async function requestMainItems() {
        rqIdCounter.current++;
        const rqId = rqIdCounter.current;
        //console.log("rqId", rqId);

        setStatus("mainLoad")
        try {
            if (!searchText) {
                setFilteredItems([]);
                setSelectedItemId(0);
                setRefsData({});
                setTotalItemCount(0);
                setStatus("idle");
                return;
            }

            //await new Promise(resolve => setTimeout(resolve, 3500)); // temp!
            const data = await ServerManager.getSimpleModeFilteredList("artists", ITEMS_PER_RQ_PAGE, searchText, "id", "asc", 0, filterExtraParams);
            if (rqIdCounter.current === rqId) {
                setFilteredItems(data["artists"]);
                setSelectedItemId(0);
                setRefsData(data.refs);
                setTotalItemCount(data.total);
                setStatus("idle");
            }
        } catch (error) {
            if (rqIdCounter.current === rqId) {
                setMainErrorMessage(`Ошибка при загрузке данных: ${error}`);
                setStatus("mainError");
            }
        } finally {
            if (rqIdCounter.current !== rqId) {
                //console.log("ignored! rqId", rqId);
            }
        }
    }

    // запуск/продление временной задержки во время ввода текста
    useEffect(() => {
        async function wait() {
            const leftTime = lastTypingTime + TYPING_TIMEOUT - Date.now();
            if (leftTime > 0)
                await new Promise(resolve => setTimeout(resolve, leftTime));
            if (!ignore)
                requestMainItems();
        }

        if (status !== "typing")
            return;

        let ignore = false;
        wait();
        return () => {
            ignore = true;
        }

    }, [status, lastTypingTime]);

    function handleMainRoleIdChange(value) {
        const newFilterExtraParams = {...filterExtraParams};
        if (value === 0)
            //delete newFilterExtraParams.main_role;
            newFilterExtraParams.main_role = "writer,composer,painter";
        else
            newFilterExtraParams.main_role = value;

        setMainRoleId(value);
        setFilterExtraParams(newFilterExtraParams);
        onFilterEdit(true);
    }

    function renderInfoMessage() {
        if (status === "mainLoad" || status === "typing")
            return <p className={cl.infoMessage}>&nbsp;</p>

        if (!searchText)
            return <p className={cl.infoMessage}>Поисковый запрос не введен!</p>

        if (filteredItems.length === 0)
            return <p className={cl.infoMessage}>Варианты не найдены</p>

        if (totalItemCount > ITEMS_PER_RQ_PAGE)
            return <p className={`${cl.infoMessage} ${cl.warnMessage}`}>Слишком много вариантов (показана лишь часть)! Уточните поисковый запрос.</p>

        return <p className={cl.infoMessage}>Выберите один из вариантов (кликнув по нему), если он подходит для связывания</p>
    }

    return (
        <BaseSimpleItemEditorForm>
            <SuggestionBlock
                initData={initData}
            />
            <InfoBlock>
                Выполните поиск (ниже) заявленного автора (по имени) в базе Культураты
                <br/>
                Обратите внимание, что заявитель мог допустить неточность в написании имени или опечатку. При
                необходимости скорректируйте поисковый запрос.
                <br/>
                <br/>
                Если заявленный автор найден в базе, то заявку следует связать с найденным автором (выделите найденного
                автора и нажмите кнопку <b>СВЯЗАТЬ&nbsp;ЗАЯВКУ</b>)
                <br/>
                <br/>
                Если заявленного автора не удается найти в базе, перейдите к следующему шагу (кнопка <b>ДАЛЕЕ</b>)
            </InfoBlock>
            <FormBlock title="Поиск автора в базе Культураты">
                <TextField
                    variant="outlined"
                    size="small"
                    label={"Поиск по имени"}
                    value={searchText}
                    onChange={(e) => {
                        setSearchText(e.target.value)
                        onFilterEdit();
                    }}
                    sx={{backgroundColor: 'white', maxWidth: "500px"}}
                    slotProps={{
                        htmlInput: {
                            maxLength: 255,
                        },
                        input: {
                            endAdornment: searchText ? (<InputAdornment position="end">
                                <button
                                    style={{
                                        cursor: "pointer",
                                        background: "none",
                                        border: "none",
                                        width: "24px",
                                        height: "24px",
                                        fontSize: "18px",
                                        color: theme.palette.primary.main
                                    }}
                                    onClick={e => {
                                        setSearchText("")
                                        onFilterEdit(true);
                                    }}
                                >
                                    ✖
                                </button>
                            </InputAdornment>) : undefined
                        },
                    }}
                />
                <ItemBrowserHeaderSimpleSelect
                    label="Род деятельности"
                    options={mainRoleOptions}
                    value={mainRoleId}
                    setValue={handleMainRoleIdChange}
                    sx={{maxWidth: "200px"}}
                />

                {(status === "mainLoad" || status === "typing") ? <LinearProgress/> : <div style={{height: "4px"}}/>}

                <p className={blockCl.title}>Результат поиска</p>

                {status === "mainError" && <Alert variant="outlined" severity="error">{mainErrorMessage}</Alert>}
                {status !== "mainError" && renderInfoMessage()}

                {filteredItems.length > 0 &&
                    <div style={{display: "flex", flexDirection: "column", gap: "10px", marginBottom: "5px"}}>
                        {filteredItems.map(item => renderItem(item, refsData, selectedItemId, setSelectedItemId))}
                    </div>}

                {/*резервируем место под 1 элемент, чтобы после поиска можно было увидеть результаты*/}
                {filteredItems.length === 0 && <div style={{height: "105px"}}/>}

            </FormBlock>
        </BaseSimpleItemEditorForm>
    );
};

function renderItem(item, refsData, selectedItemId, setSelectedItemId) {
    return <ArtistItem
        key={item.id}
        id={item.id}
        title={getFullName(item.first_name, item.last_name, item.middle_name)}
        subtitle={getItemSubtitle(item, refsData)}
        avaUrl={item.image_url}
        selected={item.id === selectedItemId}
        onClick={() => setSelectedItemId(item.id)}
    />
}

function getItemSubtitle(item, refsData) {
    const roleName = allArtistRoles.find(x => x.id === item.main_role).name;
    const countryName = refsData?.countries?.[item.country_id]?.name ?? "";
    const lifeYears = getLifeYears(item.year_of_birth, item.year_of_death);
    return `${roleName}${countryName ? `, ${countryName}` : ''}${lifeYears ? `, ${lifeYears}` : ''}`;
}

function getLifeYears(yearOfBirth, yearOfDeath) {
    if (!yearOfBirth)
        return "";

    if (!yearOfDeath)
        return `${yearOfBirth} г.р.`;

    return `(${yearOfBirth} - ${yearOfDeath})`;
}

const mainRoleOptions = [{id: 0, name: "(все)"}, ...allSuggestArtistRoles];

export default ArtistSuggestionsItemFormStep2;